import React, { useState } from 'react';
import { api } from '@/api/client';
import type { User } from '@/types';
import { platformRoleLabel } from '@/utils/workspaceRoles';
import { normalizePlatformRole, STAFF_PLATFORM_ROLE_OPTIONS } from '@/utils/teamRoles';
import { ToggleField } from '@/components/ui';

type Props = {
  user: User;
  currentUserId: string | null | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export default function EditPlatformUserModal({ user, currentUserId, onClose, onSaved }: Props) {
  const isSelf = currentUserId === user.id;
  const [email, setEmail] = useState(user.email || '');
  const [name, setName] = useState(user.name || '');
  const [role, setRole] = useState(normalizePlatformRole(user.role));
  const [enabled, setEnabled] = useState(user.is_enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.users.update(user.id, {
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        is_enabled: enabled,
      });
      onSaved('Platform user updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="edit-platform-user-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="edit-platform-user-title">Edit platform user</h2>
        <p className="muted">
          Platform permissions only. Workspace memberships are managed from the workspace Users page.
        </p>
        {user.organizationNames && user.organizationNames.length > 0 ? (
          <p className="muted">
            Workspace memberships: {user.organizationNames.join(', ')}
          </p>
        ) : null}
        <form onSubmit={save}>
          <label>
            Email
            <input value={email} disabled={isSelf} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Platform role
            <select value={role} disabled={isSelf} onChange={(e) => setRole(e.target.value)}>
              {STAFF_PLATFORM_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {!isSelf ? (
            <ToggleField checked={enabled} onChange={setEnabled} label="Account enabled" />
          ) : (
            <p className="muted">Current role: {platformRoleLabel(role)}</p>
          )}
          {error ? <div className="alert alert-error">{error}</div> : null}
          <div className="table-action-bar">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
